import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Spin, Button, Row, Col } from 'antd';
import UserStore from '../../../stores/UserStore';
import { GAEvent } from '../../../utils/tracking';
import { isFreeEmailDomain } from '../../../utils/utils';
import type { ColProps } from 'antd';

interface IProps {
    first_name?: string;
    last_name?: string;
    email?: string;
    company?: string;
    handleLogin?: () => void;
    onRegisterSuccess?: () => void;
}
const defaultColProps: ColProps = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 12,
    xxl: 12,
};

const SignUp: React.FC<IProps> = props => {
    const [loading, setLoading] = useState(false);
    const [referral, setReferral] = useState<string>('');
    const [emailDomains, setEmailDomains] = useState<string[]>([]);
    const [form] = Form.useForm();

    useEffect(() => {
        UserStore.getEmailDomains()
            .then(res => {
                const domainList = res.results?.map((item: { id: number; domain: string }) =>
                    item?.domain?.toLowerCase()
                );
                setEmailDomains(domainList);
            })
            .catch(err => {
                console.error('Error fetching email domains:', err);
            });
    }, []);

    useEffect(() => {
        if (props.first_name || props.last_name || props.email || props.company) {
            form.setFieldsValue({
                first_name: props.first_name || '',
                last_name: props.last_name || '',
                email: props.email || UserStore.guest_email || '',
                company: props.company || '',
            });
        }
    }, [props.first_name, props.last_name, props.email, props.company]);

    const submitSignUp = async (values: any) => {
        setLoading(true);

        GAEvent('Form', 'Sign Up');

        const utmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
        const setup_token = localStorage.getItem('setup_token') || undefined;

        const data: Record<string, unknown> = {
            first_name: values.first_name,
            last_name: values.last_name,
            company: values.company,
            email: values.email?.toLowerCase(),
            password1: values.password1,
            password2: values.password2,
            referral: values.referral,
            referral_answer: values.referral_answer,
            campaign_tracking: utmParams,
        };

        if (setup_token) {
            data.setup_token = setup_token;
        }

        await UserStore.register(data, props?.onRegisterSuccess)
            .then(() => {
                if (props?.handleLogin) {
                    props.handleLogin();
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Form form={form} onFinish={submitSignUp} layout={'vertical'} className="w-100">
            <Row gutter={[8, 0]}>
                <Col {...defaultColProps}>
                    <Form.Item
                        name="first_name"
                        label={'First Name'}
                        hasFeedback
                        rules={[{ required: true, message: 'Please insert a value' }]}
                        initialValue={props?.first_name || ''}
                        className="mb-0"
                    >
                        <Input autoComplete={'off'} disabled={loading} />
                    </Form.Item>
                </Col>

                <Col {...defaultColProps}>
                    <Form.Item
                        name="last_name"
                        label={'Last Name'}
                        hasFeedback
                        rules={[{ required: true, message: 'Please insert a value' }]}
                        initialValue={props?.last_name || ''}
                        className="mb-0"
                    >
                        <Input autoComplete={'off'} disabled={loading} />
                    </Form.Item>
                </Col>

                <Col {...defaultColProps}>
                    <Form.Item
                        name="email"
                        label={'Email'}
                        hasFeedback
                        validateTrigger={'onBlur'}
                        rules={[
                            {
                                type: 'email',
                                required: true,
                                message: 'Please insert a valid email',
                            },
                        ]}
                        initialValue={props?.email || UserStore.guest_email}
                        className="mb-0"
                    >
                        <Input
                            autoComplete={'username'}
                            disabled={loading}
                            onChange={() => {
                                if (form.isFieldTouched('company')) {
                                    form.validateFields(['company']);
                                }
                            }}
                        />
                    </Form.Item>
                </Col>

                <Col {...defaultColProps}>
                    <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.email !== curValues.email}>
                        {({ getFieldValue }) => (
                            <Form.Item
                                name="company"
                                label={
                                    isFreeEmailDomain(getFieldValue('email'), emailDomains)
                                        ? 'Company (Optional)'
                                        : 'Company'
                                }
                                hasFeedback
                                rules={[
                                    {
                                        required: !isFreeEmailDomain(getFieldValue('email'), emailDomains),
                                        message: 'Please insert a value',
                                    },
                                ]}
                                initialValue={props?.company || ''}
                                className="mb-0"
                            >
                                <Input autoComplete={'off'} disabled={loading} />
                            </Form.Item>
                        )}
                    </Form.Item>
                </Col>

                <Col {...defaultColProps}>
                    <Form.Item
                        name="password1"
                        label={'Password'}
                        hasFeedback
                        rules={[
                            {
                                required: true,
                                message: 'Password is required',
                            },
                            {
                                min: 8,
                                message: 'Password must have at least 8 characters',
                            },
                        ]}
                        className="mb-0"
                    >
                        <Input.Password type={'password'} autoComplete={'current-password'} disabled={loading} />
                    </Form.Item>
                </Col>

                <Col {...defaultColProps}>
                    <Form.Item
                        name="password2"
                        label={'Confirm Password'}
                        hasFeedback
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm your password!',
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password1') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match'));
                                },
                            }),
                        ]}
                        className="mb-0"
                    >
                        <Input.Password type={'password'} autoComplete={'new-password'} disabled={loading} />
                    </Form.Item>
                </Col>

                <Col span={24}>
                    <Form.Item name="referral" label={'How Did You Hear About Us?'} hasFeedback>
                        <Select onSelect={(value: string) => setReferral(value)} disabled={loading}>
                            <Select.Option value="Web search">Web search</Select.Option>
                            <Select.Option value="Online Advertisement">Online Advertisement</Select.Option>
                            <Select.Option value="A PartsBadger Sales Representative">
                                A PartsBadger Sales Representative
                            </Select.Option>
                            <Select.Option value="A Colleague">A Colleague</Select.Option>
                            <Select.Option value="Other Online Source">Other Online Source</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>

                {referral == 'A PartsBadger Sales Representative' ? (
                    <Col span={24}>
                        <Form.Item
                            name="referral_answer"
                            label={'Please enter the sales rep’s name or email'}
                            hasFeedback
                            validateTrigger={'onBlur'}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please insert a valid name or user',
                                },
                            ]}
                        >
                            <Input maxLength={70} disabled={loading} />
                        </Form.Item>
                    </Col>
                ) : null}
            </Row>
            <Button type="primary" loading={loading} htmlType="submit" block>
                Register
            </Button>
        </Form>
    );
};

export default SignUp;
